import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Link from 'next/link';

import { IProduct } from '~/lib/data-contract';
import { productImageSrc } from '$utils/product-image-src';
import { parseProductContent } from '$templates/pages/components/P50ProductDetailPage/components/pdp-tabs/pdp-relationships/product-data-parser';
import { Headline } from "$shared/components/headline";
import { AddToBasketProvider } from '$shared/utils/add-to-basket/AddToBasketProvider';
import { ProductLineCtas } from '$shared/components/product-line/product-line-ctas';
import { useAuthentication } from '$shared/hooks/useAuthentication';
import { usePriceAndStockCount } from "$utils/product/hooks/use-price-and-stock-count";
import {useProductCompare} from "$features/product-compare/product-compare.store";

type ProductComparePageProps = {
    products?: IProduct[];
    onRemoveProduct?: (product: IProduct) => void;
    onClose?: () => void;
};

type NormalizedSpecification = {
    key: string;
    label: string;
    value: string;
    group: string;
};

type ComparisonRow = {
    key: string;
    label: string;
    group: string;
    values: string[];
};

const getProductId = (product: IProduct): string =>
    product.originalItemNumber ||
    product.sanitizedItemNumber ||
    product.alternativeItemNumber ||
    product.id ||
    '';

const getProductName = (product: IProduct): string => {
    const description = [
        product.description1,
        product.description2,
        product.description3,
    ]
        .filter(Boolean)
        .join(' - ');

    return (
        description ||
        product.highlights?.productName ||
        product.originalItemNumber ||
        'Product'
    );
};

const getProductUrl = (
    product: IProduct
): string | undefined =>
    product.url ||
    (product.sanitizedItemNumber
        ? `/${product.sanitizedItemNumber}`
        : undefined);

const deduplicateSlashSeparated = (
    value: string
): string => {
    const seen = new Set<string>();

    return value
        .split('/')
        .map((part) => part.trim())
        .filter((part) => {
            if (!part) {
                return false;
            }

            const normalizedPart = part.toLowerCase();

            if (seen.has(normalizedPart)) {
                return false;
            }

            seen.add(normalizedPart);

            return true;
        })
        .join(' / ');
};

const getProductSpecifications = (
    product: IProduct
): NormalizedSpecification[] => {
    const result: NormalizedSpecification[] = [];

    product.propertyDetails?.propertyDetails?.forEach(
        (property) => {
            const propertyKey =
                property.code ||
                property.displayName;

            if (!propertyKey) {
                return;
            }

            result.push({
                key: `property-${propertyKey}`,
                label:
                    property.displayName ||
                    property.code ||
                    propertyKey,
                value: deduplicateSlashSeparated(
                    String(property.value || '')
                ),
                group: 'Specifications',
            });
        }
    );

    product.specifications?.forEach(
        (specification) => {
            if (!specification.name) {
                return;
            }

            result.push({
                key: `specification-${specification.name}`,
                label: specification.name,
                value: deduplicateSlashSeparated(
                    `${specification.value || ''} ${
                        specification.suffix || ''
                    }`.trim()
                ),
                group: 'Specifications',
            });
        }
    );

    product.productSpecifications?.forEach(
        (specification) => {
            if (
                !specification.data ||
                specification.type?.toLowerCase() === 'iso'
            ) {
                return;
            }

            try {
                const parsedData =
                    typeof specification.data === 'string'
                        ? JSON.parse(specification.data)
                        : specification.data;

                if (
                    !parsedData ||
                    typeof parsedData !== 'object'
                ) {
                    return;
                }

                Object.entries(parsedData).forEach(
                    ([key, value]) => {
                        if (key === 'id') {
                            return;
                        }

                        if (
                            typeof value === 'object' &&
                            value !== null &&
                            'value' in value
                        ) {
                            const typedValue = value as {
                                value?: string | number;
                                unit?: string;
                                description?: string;
                            };

                            result.push({
                                key: `${
                                    specification.type ||
                                    'other'
                                }-${key}`,
                                label:
                                    typedValue.description
                                        ? `${typedValue.description} (${key})`
                                        : key,
                                value: `${
                                    typedValue.value ?? ''
                                } ${
                                    typedValue.unit || ''
                                }`.trim(),
                                group:
                                    specification.type ||
                                    'Other',
                            });

                            return;
                        }

                        result.push({
                            key: `${
                                specification.type ||
                                'other'
                            }-${key}`,
                            label: key,
                            value:
                                value === null ||
                                value === undefined
                                    ? ''
                                    : String(value),
                            group:
                                specification.type ||
                                'Other',
                        });
                    }
                );
            } catch (error) {
                console.error(
                    `Error parsing specification data for ${specification.type}`,
                    error
                );
            }
        }
    );

    const uniqueSpecifications =
        new Map<string, NormalizedSpecification>();

    result.forEach((specification) => {
        const existing =
            uniqueSpecifications.get(
                specification.key
            );

        if (
            !existing ||
            (!existing.value &&
                specification.value)
        ) {
            uniqueSpecifications.set(
                specification.key,
                specification
            );
        }
    });

    return Array.from(
        uniqueSpecifications.values()
    );
};

const buildComparisonRows = (
    products: IProduct[]
): ComparisonRow[] => {
    const specificationMaps = products.map(
        (product) => {
            const specifications =
                getProductSpecifications(product);

            return new Map(
                specifications.map(
                    (specification) => [
                        specification.key,
                        specification,
                    ]
                )
            );
        }
    );

    const allKeys = Array.from(
        new Set(
            specificationMaps.flatMap(
                (specificationMap) =>
                    Array.from(
                        specificationMap.keys()
                    )
            )
        )
    );

    return allKeys.map((key) => {
        const firstSpecification =
            specificationMaps
                .map((specificationMap) =>
                    specificationMap.get(key)
                )
                .find(Boolean);

        return {
            key,
            label:
                firstSpecification?.label ||
                key,
            group:
                firstSpecification?.group ||
                'Other',
            values: specificationMaps.map(
                (specificationMap) =>
                    specificationMap.get(key)
                        ?.value || '—'
            ),
        };
    });
};

const valuesAreDifferent = (
    values: string[]
): boolean => {
    const normalizedValues =
        values.map((value) =>
            value.trim().toLowerCase()
        );

    return (
        new Set(normalizedValues).size > 1
    );
};

const getTechnicalDrawingUrl = (
    product: IProduct
): string | undefined => {
    const productDataImages =
        product.productData?.flatMap(
            (item) => {
                const parsedContent =
                    parseProductContent(
                        item.content
                    );

                if (
                    !Array.isArray(
                        parsedContent
                    )
                ) {
                    return [];
                }

                return parsedContent.filter(
                    (
                        source: unknown
                    ): source is string =>
                        typeof source ===
                        'string' &&
                        /\.(png|jpg|jpeg|gif|webp)(\?.*)?$/i.test(
                            source
                        )
                );
            }
        ) ?? [];

    const technicalDrawingPath =
        productDataImages[0];

    return technicalDrawingPath
        ? productImageSrc(
            technicalDrawingPath
        )
        : undefined;
};

export const ProductComparePage = ({
                                       products = [],
                                       onRemoveProduct,
                                   }: ProductComparePageProps) => {
    const [
        showDifferencesOnly,
        setShowDifferencesOnly,
    ] = useState(false);

    const { isAuthenticated, userFound } = useAuthentication();
    const closeCompareForNavigation = useProductCompare(
        (state) => state.closeCompareForNavigation
    );
    const itemNumbers = useMemo(
        () => products.map((product) => product.originalItemNumber || ''),
        [products]
    );

    const { productPricesAndStockCounts, isBusy } = usePriceAndStockCount(
        ['ProductComparePage', ...itemNumbers],
        itemNumbers
    );

    const rows = useMemo(
        () =>
            buildComparisonRows(products),
        [products]
    );

    const visibleRows = useMemo(() => {
        if (!showDifferencesOnly) {
            return rows;
        }

        return rows.filter((row) =>
            valuesAreDifferent(
                row.values
            )
        );
    }, [
        rows,
        showDifferencesOnly,
    ]);

    const groups = useMemo(() => {
        const groupedRows =
            new Map<
                string,
                ComparisonRow[]
            >();

        visibleRows.forEach((row) => {
            const existingRows =
                groupedRows.get(
                    row.group
                ) || [];

            existingRows.push(row);

            groupedRows.set(
                row.group,
                existingRows
            );
        });

        return Array.from(
            groupedRows.entries()
        );
    }, [visibleRows]);

    if (!products.length) {
        return (
            <Page>
                <Headline
                    as="h2"
                    size={2}
                >
                    Compare products
                </Headline>
                <EmptyState>
                    No products selected for
                    comparison.
                </EmptyState>
            </Page>
        );
    }

    return (
        <Page>
            <Header>
                <Headline
                    as="h2"
                    size={2}
                    noMargin
                >
                    Compare products
                </Headline>

                <DifferenceToggle>
                    <span>Show differences only</span>

                    <input
                        type="checkbox"
                        role="switch"
                        checked={showDifferencesOnly}
                        onChange={(event) =>
                            setShowDifferencesOnly(event.target.checked)
                        }
                    />

                    <span className="toggle-track" aria-hidden="true">
                        <span className="toggle-thumb">
                            {showDifferencesOnly && (
                                <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
                                    <path
                                        d="M3 8.5L6.5 12L13 4"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </span>
                    </span>
                </DifferenceToggle>
            </Header>

            {!rows.length && (
                <StatusMessage>
                    No specifications are
                    available for the selected
                    products.
                </StatusMessage>
            )}

            <TableWrapper>
                <ComparisonTable>
                    <thead>
                    <tr>
                        <SpecificationHeader/>

                        {products.map(
                            (
                                product,
                                index
                            ) => {
                                const productId =
                                    getProductId(
                                        product
                                    );

                                const name =
                                    getProductName(
                                        product
                                    );

                                const productUrl =
                                    getProductUrl(
                                        product
                                    );

                                const photoUrl =
                                    product.photoPath
                                        ? productImageSrc(
                                            product.photoPath
                                        )
                                        : undefined;

                                const technicalDrawingUrl =
                                    getTechnicalDrawingUrl(
                                        product
                                    );

                                return (
                                    <ProductHeader
                                        key={
                                            productId ||
                                            index
                                        }
                                    >
                                        {onRemoveProduct && (
                                            <RemoveProductButton
                                                type="button"
                                                aria-label={`Remove ${productId} from comparison`}
                                                onClick={() =>
                                                    onRemoveProduct(product)
                                                }
                                            >
                                                ×
                                            </RemoveProductButton>
                                        )}

                                        <ProductHeaderContent>
                                            {productUrl ? (
                                                <ProductNumberLinkWrapper>
                                                    <Link href={productUrl} passHref>
                                                        <a
                                                            title={productId}
                                                            onClick={closeCompareForNavigation}
                                                        >
                                                            {productId}
                                                        </a>
                                                    </Link>
                                                </ProductNumberLinkWrapper>
                                            ) : (
                                                <ProductNumber>
                                                    {productId}
                                                </ProductNumber>
                                            )}

                                            <ProductName title={name}>
                                                {name}
                                            </ProductName>

                                            {photoUrl && (
                                                productUrl ? (
                                                    <Link href={productUrl} passHref>
                                                        <a onClick={closeCompareForNavigation}>
                                                            <ImageWrapper>
                                                                <ProductImage
                                                                    src={photoUrl}
                                                                    alt={name}
                                                                />
                                                            </ImageWrapper>
                                                        </a>
                                                    </Link>
                                                ) : (
                                                    <ImageWrapper>
                                                        <ProductImage
                                                            src={photoUrl}
                                                            alt={name}
                                                        />
                                                    </ImageWrapper>
                                                )
                                            )}

                                            {technicalDrawingUrl && (
                                                productUrl ? (
                                                    <Link href={productUrl} passHref>
                                                        <a onClick={closeCompareForNavigation}>
                                                            <ImageWrapper>
                                                                <ProductImage
                                                                    src={technicalDrawingUrl}
                                                                    alt={`${name} technical drawing`}
                                                                />
                                                            </ImageWrapper>
                                                        </a>
                                                    </Link>
                                                ) : (
                                                    <ImageWrapper>
                                                        <ProductImage
                                                            src={technicalDrawingUrl}
                                                            alt={`${name} technical drawing`}
                                                        />
                                                    </ImageWrapper>
                                                )
                                            )}

                                            {isAuthenticated && userFound && (
                                                <CompareBasketWrapper>
                                                    <AddToBasketProvider
                                                        {...product}
                                                        {...productPricesAndStockCounts.get(product.originalItemNumber || '')}
                                                        isQuantityReady={
                                                            !isBusy &&
                                                            !!productPricesAndStockCounts.get(product.originalItemNumber || '')?.minimumOrderSize
                                                        }
                                                    >
                                                        <ProductLineCtas />
                                                    </AddToBasketProvider>
                                                </CompareBasketWrapper>
                                            )}
                                        </ProductHeaderContent>
                                    </ProductHeader>
                                );
                            }
                        )}
                    </tr>
                    </thead>

                    <tbody>
                    {groups.map(
                        ([
                             groupName,
                             groupRows,
                         ]) => (
                            <React.Fragment
                                key={
                                    groupName
                                }
                            >
                                <GroupRow>
                                    <GroupCell
                                        colSpan={
                                            products.length +
                                            1
                                        }
                                    >
                                        <Headline
                                            as="h3"
                                            size={3}
                                            noMargin
                                        >
                                            {groupName}
                                        </Headline>
                                    </GroupCell>
                                </GroupRow>

                                {groupRows.map(
                                    (row) => {
                                        const different =
                                            valuesAreDifferent(
                                                row.values
                                            );

                                        return (
                                            <SpecificationRow
                                                key={
                                                    row.key
                                                }
                                                data-different={
                                                    different
                                                }
                                            >
                                                <SpecificationName>
                                                    {
                                                        row.label
                                                    }
                                                </SpecificationName>

                                                {row.values.map(
                                                    (
                                                        value,
                                                        index
                                                    ) => (
                                                        <ValueCell
                                                            key={`${row.key}-${index}`}
                                                        >
                                                            {
                                                                value
                                                            }
                                                        </ValueCell>
                                                    )
                                                )}
                                            </SpecificationRow>
                                        );
                                    }
                                )}
                            </React.Fragment>
                        )
                    )}
                    </tbody>
                </ComparisonTable>
            </TableWrapper>
        </Page>
    );
};

export default ProductComparePage;

const Page = styled.main`
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 40px 24px 80px;

    @media (max-width: 768px), (max-height: 500px) {
        padding: 16px 12px 40px;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 24px;
    margin-bottom: 32px;

    @media (max-width: 768px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const DifferenceToggle = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 14px;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    -webkit-user-select: none;

    input {
        position: absolute;
        opacity: 0;
        width: 1px;
        height: 1px;
    }

    .toggle-track {
        position: relative;
        display: block;
        width: 40px;
        height: 22px;
        flex-shrink: 0;
        border-radius: 20px;
        background: #ababab;
        transition: background 0.2s;
    }

    .toggle-thumb {
        position: absolute;
        top: 2px;
        left: 2px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: white;
        color: #9ca3af;
        font-size: 12px;
        font-weight: 700;
        transition: transform 0.2s;
    }

    input:checked + .toggle-track {
        background: #168bd2;
    }

    input:checked + .toggle-track .toggle-thumb {
        transform: translateX(18px);
        color: #168bd2;
    }

    input:focus-visible + .toggle-track {
        outline: 2px solid #168bd2;
        outline-offset: 3px;
    }
`;

const StatusMessage = styled.div`
    margin-bottom: 20px;
    padding: 14px 16px;
    background: #f5f5f5;
`;

const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
`;

const ComparisonTable = styled.table`
    width: 100%;
    min-width: 900px;
    border-collapse: collapse;
    table-layout: fixed;

    th,
    td {
        padding: 16px;
        text-align: left;
        vertical-align: top;
        border-bottom: 1px solid #ddd;
    }

    @media (max-width: 768px), (max-height: 500px) {
        width: max-content;
        min-width: 100%;
        table-layout: fixed;

        th,
        td {
            padding: 10px;
        }
    }
`;

const SpecificationHeader = styled.th`
    position: sticky;
    left: 0;
    z-index: 3;
    width: 280px;
    min-width: 280px;
    background: white;

    @media (max-width: 768px) {
        width: 130px;
        min-width: 130px;
        max-width: 130px;
    }
`;

const ProductHeader = styled.th`
    position: relative;
    min-width: 250px;

    @media (max-width: 768px) {
        width: 160px;
        min-width: 160px;
        max-width: 160px;
    }
`;

const RemoveProductButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 32px;
    height: 32px;
    padding: 0;

    border: 0;
    background: transparent;

    font-size: 24px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        background: #f3f3f3;
    }
`;

const ProductHeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (max-height: 500px) {
        gap: 8px;
    }
`;

const ImageWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 260px;
    cursor: pointer;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.08);
        pointer-events: none;
    }

    @media (max-width: 768px) {
        height: 120px;
    }

    @media (max-height: 500px) {
        height: 100px;
    }
`;

const ProductImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: #ffffff;
`;

const ProductNumber = styled.div`
    min-height: 22px;
    font-size: 18px;
    font-weight: 700;
`;

const ProductNumberLinkWrapper = styled.div`
    font-size: 18px;
    font-weight: 700;

    a {
        color: inherit;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const ProductName = styled.div`
    font-family: inherit;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.5;
    color: inherit;

    height: 45px;
    overflow: hidden;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;

    @media (max-height: 500px) {
        height: 20px;
        -webkit-line-clamp: 1;
    }
`;
const GroupRow = styled.tr``;

const GroupCell = styled.th`
    padding-top: 28px !important;
    padding-bottom: 12px !important;
    background: #f8f8f8;

    @media (max-height: 500px) {
        padding-top: 14px !important;
        padding-bottom: 8px !important;
    }
`;

const SpecificationRow = styled.tr`
    &[data-different='true'] {
        background: #f8f8f8;
    }
`;

const SpecificationName = styled.th`
    position: sticky;
    left: 0;
    z-index: 2;
    width: 280px;
    min-width: 280px;
    background: white;
    font-weight: 600;

    @media (max-width: 768px) {
        width: 130px;
        min-width: 130px;
        max-width: 130px;
        font-size: 12px;
        word-break: break-word;
    }
`;

const ValueCell = styled.td`
    word-break: break-word;

    @media (max-width: 768px) {
        width: 160px;
        min-width: 160px;
        max-width: 160px;
        font-size: 12px;
    }
`;

const EmptyState = styled.div`
    padding: 60px 0;
`;

const CompareBasketWrapper = styled.div`
    width: 100%;
    min-width: 0;

    display: grid;
    grid-template-columns: 24px auto;
    column-gap: 8px;
    align-items: center;
    justify-content: end;

    > div:first-of-type {
        grid-column: 1;
        grid-row: 1;
    }

    > div:nth-of-type(2) {
        grid-column: 2;
        grid-row: 1;
        min-width: 0;
    }

    > div:nth-of-type(3) {
        grid-column: 2;
        grid-row: 2;
    }

    @media (max-width: 768px) {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-rows: auto auto auto;
        gap: 6px;
        justify-content: stretch;
        align-items: center;

        > div:nth-of-type(2) {
            display: contents;
        }

        > div:nth-of-type(2) > div {
            grid-column: 1 / -1;
            grid-row: 1;
            min-width: 0;
            max-width: 100%;
        }

        > div:nth-of-type(3) {
            grid-column: 1 / -1;
            grid-row: 2;
            font-size: 10px;
        }

        > div:first-of-type {
            grid-column: 1;
            grid-row: 3;
            justify-self: start;
        }

        > div:nth-of-type(2) > button {
            grid-column: 2;
            grid-row: 3;
            justify-self: end;
        }
    }
`;